import { cast, detach, flow, getSnapshot, SnapshotOut, toGenerator, types } from "mobx-state-tree";
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatus, OperationStatusModel, OperationSuccess } from "models/common/OperationStatus";
import { Profile, ProfileModel } from "models/ota/ProfileModel";
import type { ProfileModuleVersion } from "models/ota/ProfileModuleVersionModel";
import oversight from "services/oversight";

export const ProfilesStore = types.model('ProfilesStore', {
  profiles: types.array(ProfileModel),
  fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  refreshStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  // map of profile id to the fetch status of its module versions
  fetchModuleVersionsStatuses: types.map(OperationStatusModel),
})
.views(self => ({
  get isLoading() {
    return self.fetchStatus.inProgress
  },
  getProfile(profileId: string): Profile | undefined {
    return self.profiles.find(profile => profile.id === profileId)
  },
}))
.views(self => ({
  // null when the module versions of this profile were not fetched yet
  getModuleVersions(profileId: string): ProfileModuleVersion[] | null {
    return self.getProfile(profileId)?.module_versions ?? null
  },
}))
.actions(self => {
  // Detaching the previous status keeps it accessible to whoever still holds it (see VehiclesStore)
  const _updateFetchModuleVersionsStatus = (profileId: string, status: OperationStatus) => {
    const oldStatus = self.fetchModuleVersionsStatuses.get(profileId)
    if (oldStatus !== undefined) {
      detach(oldStatus)
    }
    self.fetchModuleVersionsStatuses.set(profileId, status)
  }

  const fetchProfiles = flow(function* fetchProfiles() {
    self.fetchStatus = OperationInProgress()
    const response = yield* toGenerator(oversight.getProfiles())

    if (response.error) {
      self.fetchStatus = OperationFailure(response.error)
      return
    }

    // The profiles list holds short profiles only, so keep the module versions we already fetched
    const loadedModuleVersions = new Map<string, SnapshotOut<typeof ProfileModel>['module_versions']>(
      self.profiles.map(profile => [profile.id, getSnapshot(profile).module_versions])
    )
    self.profiles = cast(response.result.map(profile => ({
      ...profile,
      module_versions: loadedModuleVersions.get(profile.id) ?? null,
    })))
    self.fetchStatus = OperationSuccess()
  })

  const fetchModuleVersions = flow(function* fetchModuleVersions(profileId: string) {
    _updateFetchModuleVersionsStatus(profileId, OperationInProgress())
    const response = yield* toGenerator(oversight.getProfile(profileId))

    if (response.error) {
      _updateFetchModuleVersionsStatus(profileId, OperationFailure(response.error))
      return
    }

    // Profiles that are not in the profiles list (archived, or list not fetched yet) are not stored
    const profile = self.getProfile(profileId)
    if (profile !== undefined) {
      profile.module_versions = cast(response.result.module_versions)
    }
    _updateFetchModuleVersionsStatus(profileId, OperationSuccess())
  })

  return {
    fetchProfiles,
    fetchModuleVersions,
    // Fetched once, and again only after failing
    fetchProfilesIfNeeded() {
      if (self.fetchStatus.isSuccess || self.fetchStatus.inProgress) {
        return
      }
      void fetchProfiles()
    },
    fetchModuleVersionsIfNeeded(profileId: string) {
      const status = self.fetchModuleVersionsStatuses.get(profileId)
      if (self.getModuleVersions(profileId) !== null || status?.inProgress) {
        return
      }
      void fetchModuleVersions(profileId)
    },
    refresh: flow(function* refresh() {
      self.refreshStatus = OperationInProgress()
      const response = yield* toGenerator(oversight.refreshProfiles())

      if (response.error) {
        self.refreshStatus = OperationFailure(response.error)
        return
      }

      self.refreshStatus = OperationSuccess()
      yield fetchProfiles()
    }),
    reset() {
      self.profiles.clear()
      self.fetchModuleVersionsStatuses.clear()
      self.fetchStatus = OperationNotStarted()
      self.refreshStatus = OperationNotStarted()
    },
  }
})
